<template>
  <div class="appFooter">
    <div class="leftSide">
      <tooltip :disabled="!capacityReservationAvailable" :text="reservationDuration">
        <div :class="['capacityReservation', capacityReservationAvailable && 'capacityReservation--success']">
          <img v-if="capacityReservationAvailable" :src="CloudSuccess" alt="Cloud Success" />
          <img v-else :src="CloudWarning" alt="Cloud Warning" />

          <span>{{ capacityReservationLabel }}</span>
        </div>
      </tooltip>
    </div>

    <div class="rightSide">
      <v-btn
        v-if="singleDownloadOption"
        @click.stop="downloadAction"
        :disabled="!app.id || loading || !isDeployed"
        large
      >
        <v-icon left>action_file_download_or_import</v-icon>
        <span>{{ $t("WorkspaceEdit.appCard.footer.buttons.download") }}</span>
      </v-btn>

      <v-menu v-else offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            :disabled="!app.id || loading || !isDeployed"
            class="no-padding downloadOptions"
            large
          >
            <div class="group">
              <v-icon left>action_file_download_or_import</v-icon>
              <span>{{ $t("WorkspaceEdit.appCard.footer.buttons.download") }}</span>
            </div>

            <v-icon :size="25">mdi-menu-down</v-icon>
          </v-btn>
        </template>

        <v-list class="otherOptionsList">
          <v-list-item @click="$emit('download:shortcut')" class="optionItem">
            <tooltip v-if="isDeployed" :text="$t('WorkspaceEdit.appCard.footer.tooltip.niceDcv')" right>
              <v-list-item-title>
                <v-icon left>action_file_download_or_import</v-icon>
                <span>{{ $t("WorkspaceEdit.appCard.footer.buttons.downloadNiceDcv") }}</span>
              </v-list-item-title>
            </tooltip>
          </v-list-item>

          <v-list-item v-if="isSaaS" @click="$emit('download:app-launcher')" class="optionItem">
            <tooltip :text="$t('WorkspaceEdit.appCard.footer.tooltip.appLauncher')" right>
              <v-list-item-title>
                <v-icon left>action_file_download_or_import</v-icon>
                <span>{{ $t("WorkspaceEdit.appCard.footer.buttons.downloadAppLauncher") }}</span>
              </v-list-item-title>
            </tooltip>
          </v-list-item>
        </v-list>
      </v-menu>

      <tooltip :disabled="!app.id || loading || !isOnline" :text="$t('LicensePage.appCard.hint.open')" bottom>
        <v-btn @click.stop="$emit('open:instance')" :disabled="!app.id || loading || !isOnline" color="primary" large>
          <v-icon left>content_folder_closed</v-icon>
          <span>{{ $t("WorkspaceEdit.appCard.footer.buttons.open") }}</span>
        </v-btn>
      </tooltip>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from "@vue/composition-api"
import { IWorkspaceAsset } from "@/types/workspace"
import { useStore } from "@/composable/useStore"
import { createTranslationModule, translatePlural } from "@/plugins/i18n"
import CloudSuccess from "@/assets/cloud-success.svg"
import CloudWarning from "@/assets/cloud-warning.svg"
import Tooltip from "@/components/tooltip/TooltipRoot.vue"
import Utils from "@/utils/utils"

export default defineComponent({
  name: "AppFooter",
  props: {
    app: {
      type: Object as PropType<IWorkspaceAsset>,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    isOnline: {
      type: Boolean,
      required: true
    },
    isDeployed: {
      type: Boolean,
      required: true
    }
  },
  setup(props, ctx) {
    const t = createTranslationModule("WorkspaceEdit.appCard.footer")
    const { adminModule } = useStore()

    const isSaaS = computed(() => {
      return adminModule.isSaaS
    })

    const reservationDuration = computed(() => {
      const reservation = props.app.capacityReservation

      if (reservation && reservation.endDate) {
        const { days, hours, min } = Utils.dateRange(Date.now(), reservation.endDate)
        const translatePrefix = "WorkspaceEdit.appCard.footer.capacityReservation.duration"

        if (days > 0) {
          return translatePlural(translatePrefix + ".day", days, { days })
        } else if (hours > 0) {
          return translatePlural(translatePrefix + ".hour", hours, { hours })
        } else if (min > 0) {
          return t("capacityReservation.duration.min", { min })
        }
      }

      return null
    })

    const capacityReservationAvailable = computed(() => {
      const reservation = props.app.capacityReservation

      if (reservation) {
        return reservation.endDate !== null
      }

      return false
    })

    const capacityReservationLabel = computed(() => {
      if (capacityReservationAvailable.value) {
        return t("capacityReservation.reserved")
      } else {
        return t("capacityReservation.onDemand")
      }
    })

    const singleDownloadOption = computed(() => {
      return !(isSaaS.value && props.isDeployed)
    })

    const downloadAction = () => {
      if (isSaaS.value) {
        ctx.emit("download:app-launcher")
      } else {
        ctx.emit("download:shortcut")
      }
    }

    return {
      reservationDuration,
      capacityReservationLabel,
      capacityReservationAvailable,
      CloudSuccess,
      CloudWarning,
      downloadAction,
      singleDownloadOption,
      isSaaS
    }
  },
  components: {
    Tooltip
  }
})
</script>
